import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { colors, typography } from '../../../../styles/theme';
import {
  flattenSchemaFields,
  isUfAttributeFieldEmpty,
  sortSchemaGroups,
} from '../../../utils/ufAttributeFormUtils';
import { withAlpha } from '../../../utils/sharedBranding';
import { sanitizePhoneInput } from '../../../../Utils/brandForm';
import {
  ORIENTATION_EMPTY_LABEL,
  buildOrientationSelectOptions,
  getOrientationLabel,
} from '../../../../Utils/compassOrientation';

const groupTitleSx = {
  color: colors.textPanelTitle,
  fontSize: '14px',
  fontWeight: typography.fontWeight.semibold,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  fontFamily: typography.fontFamily.primary,
  lineHeight: '16px',
  display: 'block',
  mb: 1.25,
};

const disabledFieldSx = {
  '& .MuiOutlinedInput-input.Mui-disabled': {
    WebkitTextFillColor: 'rgba(255,255,255,0.82)',
    color: 'rgba(255,255,255,0.82)',
  },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: 'rgba(255,255,255,0.55)',
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.10)',
  },
};

export const disabledSelectSx = {
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.82)',
  },
  '&.Mui-disabled .MuiSelect-select': {
    WebkitTextFillColor: 'rgba(255,255,255,0.82)',
    color: 'rgba(255,255,255,0.82)',
  },
  // Solo lectura: se ve como los campos de texto bloqueados (sin flecha, hover ni puntero).
  '&.Mui-disabled .MuiSelect-icon': {
    display: 'none',
  },
  '&.Mui-disabled, &.Mui-disabled .MuiSelect-select': {
    cursor: 'default',
  },
  '&.Mui-disabled .MuiSelect-select.MuiSelect-select': {
    paddingRight: '14px',
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline, &.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.10)',
  },
};

const booleanRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 1.5,
  py: 1,
  borderRadius: '8px',
  border: '1px solid #383838',
};

const booleanLabelSx = (disabled) => ({
  m: 0,
  width: '100%',
  justifyContent: 'space-between',
  ml: 0,
  mr: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontFamily: typography.fontFamily.primary,
    color: disabled ? 'rgba(255,255,255,0.78)' : colors.textPanelTitle,
  },
  ...(disabled ? {
    '&.Mui-disabled .MuiFormControlLabel-label': {
      color: 'rgba(255,255,255,0.78)',
    },
  } : {}),
});

const getSwitchSx = (accentColor = null) => {
  const thumb = accentColor || colors.primaryLight;
  const track = accentColor || colors.primary;
  const thumbDisabled = accentColor ? withAlpha(accentColor, 0.55) : 'rgba(72,146,145,0.55)';
  const trackDisabled = accentColor ? withAlpha(accentColor, 0.35) : 'rgba(72,146,145,0.35)';

  return {
    '& .MuiSwitch-switchBase.Mui-checked': { color: thumb },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: track },
    '& .MuiSwitch-switchBase.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': { color: thumbDisabled },
    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': {
      bgcolor: trackDisabled,
    },
    '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.18)' },
  };
};

/** Campos visibles en el layout horizontal de Superficies (total se oculta). */
const SURFACE_LAYOUT_FIELD_IDS = ['covered_surface', 'open_surface'];

const SURFACE_FIELD_LABELS = {
  covered_surface: 'cubierta',
  open_surface: 'semicubierta',
};

const UF_ATTRIBUTES_SKELETON_SX = {
  bgcolor: 'rgba(255,255,255,0.08)',
  borderRadius: '8px',
};

/** Placeholder alineado al layout de grupos / grid / switches de características. */
const UfAttributesSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
    <Box>
      <Skeleton variant="text" width={96} height={16} sx={UF_ATTRIBUTES_SKELETON_SX} />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 1.5 }}>
        <Skeleton variant="rounded" height={40} sx={{ ...UF_ATTRIBUTES_SKELETON_SX, flex: 1 }} />
        <Skeleton variant="rounded" height={40} sx={{ ...UF_ATTRIBUTES_SKELETON_SX, flex: 1 }} />
      </Box>
    </Box>
    <Box>
      <Skeleton variant="text" width={128} height={16} sx={UF_ATTRIBUTES_SKELETON_SX} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        {[0, 1, 2, 3].map((key) => (
          <Skeleton key={key} variant="rounded" height={40} sx={UF_ATTRIBUTES_SKELETON_SX} />
        ))}
      </Box>
    </Box>
    <Box>
      <Skeleton variant="text" width={88} height={16} sx={UF_ATTRIBUTES_SKELETON_SX} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
        {[0, 1, 2].map((key) => (
          <Skeleton key={key} variant="rounded" height={36} sx={UF_ATTRIBUTES_SKELETON_SX} />
        ))}
      </Box>
    </Box>
  </Box>
);

const HIDDEN_UF_FIELD_IDS = new Set(['total_surface']);

const ADDITIONAL_FIELD_IDS = ['unit_floor', 'year_built', 'disposition', 'orientation'];
const ADDITIONAL_FIELD_ID_SET = new Set(ADDITIONAL_FIELD_IDS);

const hideNumberSpinnerSx = {
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

const isSurfaceGroup = (group) => {
  const ids = new Set((group?.fields || []).map((f) => f.id));
  return SURFACE_LAYOUT_FIELD_IDS.every((id) => ids.has(id));
};

const orderSurfaceFields = (fields = []) => (
  SURFACE_LAYOUT_FIELD_IDS
    .map((id) => fields.find((f) => f.id === id))
    .filter(Boolean)
);

const UfAttributesForm = ({
  schema = null,
  form = {},
  setForm,
  fieldSx,
  inputLabelSx,
  selectSx,
  menuItemSx,
  listSelectMenuProps,
  disabled = false,
  loading = false,
  /** Campos que no se pueden editar aunque el form esté en modo edición. */
  lockedFieldIds = [],
  /** En lectura (shares): oculta campos vacíos y grupos sin valores. */
  hideEmpty = false,
  /** Acento de marca (vistas compartidas branded) para switches activos. */
  accentColor = null,
  /** Errores por id de campo (p. ej. 400 VALIDATION_ERROR con `field`). */
  fieldErrors = null,
}) => {
  const switchSx = getSwitchSx(accentColor);
  const lockedIds = useMemo(
    () => new Set(Array.isArray(lockedFieldIds) ? lockedFieldIds : []),
    [lockedFieldIds],
  );
  if (loading) {
    return <UfAttributesSkeleton />;
  }

  if (!schema?.groups?.length) {
    return (
      <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>
        No hay características disponibles para este tipo de inmueble.
      </Typography>
    );
  }

  const mergedFieldSx = disabled
    ? { ...fieldSx, ...hideNumberSpinnerSx, ...disabledFieldSx }
    : { ...fieldSx, ...hideNumberSpinnerSx };
  const mergedSelectSx = disabled ? { ...selectSx, ...disabledSelectSx } : selectSx;
  const mergedInputLabelSx = disabled
    ? { ...inputLabelSx, '&.Mui-disabled': { color: 'rgba(255,255,255,0.55)' } }
    : inputLabelSx;

  const updateColumnField = (fieldId, value) => {
    setForm((prev) => ({ ...prev, [fieldId]: value }));
  };

  const updateJsonField = (fieldId, value) => {
    setForm((prev) => ({
      ...prev,
      attributes: {
        ...(prev.attributes || {}),
        [fieldId]: value,
      },
    }));
  };

  const readFieldValue = (field) => {
    if (field.storage === 'column') {
      return form[field.id];
    }
    return form.attributes?.[field.id];
  };

  const shouldRenderField = (field) => {
    if (HIDDEN_UF_FIELD_IDS.has(field.id)) return false;
    if (!hideEmpty) return true;
    const value = readFieldValue(field);
    if (isUfAttributeFieldEmpty(value)) return false;
    // En share no listamos amenities en false (el payload suele omitirlos).
    if (field.type === 'boolean' && value === false) return false;
    return true;
  };

  const renderField = (field, { labelOverride } = {}) => {
    const value = readFieldValue(field);
    const label = labelOverride ?? field.label;
    const fieldLocked = disabled || lockedIds.has(field.id);
    const fieldSxForState = fieldLocked
      ? { ...fieldSx, ...hideNumberSpinnerSx, ...disabledFieldSx }
      : mergedFieldSx;
    const fieldLabelSx = fieldLocked
      ? { ...mergedInputLabelSx, '&.Mui-disabled': { color: 'rgba(255,255,255,0.55)' } }
      : mergedInputLabelSx;
    const fieldSelectSx = fieldLocked ? { ...selectSx, ...disabledSelectSx } : mergedSelectSx;
    const fieldError = fieldErrors?.[field.id] || '';
    const commonProps = {
      disabled: fieldLocked,
      size: 'small',
      fullWidth: true,
      variant: 'outlined',
      sx: fieldSxForState,
      error: Boolean(fieldError),
      helperText: fieldError || undefined,
    };

    if (field.type === 'boolean') {
      const checked = value === true;
      const onToggle = (next) => {
        if (field.storage === 'column') updateColumnField(field.id, next);
        else updateJsonField(field.id, next);
      };

      return (
        <Box key={field.id} sx={booleanRowSx}>
          <FormControlLabel
            disabled={fieldLocked}
            control={(
              <Switch
                checked={checked}
                onChange={(e) => onToggle(e.target.checked)}
                disabled={fieldLocked}
                size="small"
                sx={switchSx}
              />
            )}
            label={field.label}
            sx={booleanLabelSx(fieldLocked)}
            labelPlacement="start"
          />
        </Box>
      );
    }

    const isOrientationField = field.id === 'orientation';
    if (field.type === 'enum' || isOrientationField) {
      const currentValue = value ?? '';
      const optionsWithCurrentValue = isOrientationField
        ? buildOrientationSelectOptions(currentValue)
        : (Array.isArray(field.options) ? field.options : []);
      const getOptionLabel = (optionValue) => {
        if (isOrientationField) return getOrientationLabel(optionValue);
        const option = optionsWithCurrentValue.find((item) => {
          const itemValue = typeof item === 'string' ? item : item.value;
          return itemValue === optionValue;
        });
        if (!option) return optionValue;
        return typeof option === 'string' ? option : (option.label || option.value);
      };

      return (
        <FormControl key={field.id} size="small" fullWidth disabled={fieldLocked} error={Boolean(fieldError)}>
          <InputLabel sx={fieldLabelSx} shrink>
            {label}
          </InputLabel>
          <Select
            value={currentValue}
            label={label}
            variant="outlined"
            notched
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return isOrientationField ? ORIENTATION_EMPTY_LABEL : '';
              }
              return getOptionLabel(selected);
            }}
            onChange={(e) => {
              const next = e.target.value;
              if (field.storage === 'column') updateColumnField(field.id, next);
              else updateJsonField(field.id, next);
            }}
            sx={fieldSelectSx}
            MenuProps={listSelectMenuProps}
          >
            <MenuItem value="" sx={menuItemSx}>
              {isOrientationField ? ORIENTATION_EMPTY_LABEL : <em>Sin seleccionar</em>}
            </MenuItem>
            {optionsWithCurrentValue.map((option) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : (option.label || option.value);
              return (
                <MenuItem key={optionValue} value={optionValue} sx={menuItemSx}>
                  {optionLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    }

    if (field.type === 'date') {
      return (
        <TextField
          {...commonProps}
          label={label}
          type="date"
          value={value ?? ''}
          InputLabelProps={{ sx: fieldLabelSx, shrink: true }}
          onChange={(e) => {
            if (fieldLocked) return;
            const next = e.target.value;
            if (field.storage === 'column') updateColumnField(field.id, next);
            else updateJsonField(field.id, next);
          }}
        />
      );
    }

    if (field.type === 'string') {
      const isPhone = field.format === 'tel';
      const stringInputProps = {
        ...(field.maxLength ? { maxLength: field.maxLength } : {}),
        ...(isPhone ? { inputMode: 'tel' } : {}),
      };
      return (
        <TextField
          {...commonProps}
          label={label}
          type="text"
          value={value ?? ''}
          InputLabelProps={{ sx: fieldLabelSx }}
          inputProps={Object.keys(stringInputProps).length ? stringInputProps : undefined}
          onChange={(e) => {
            if (fieldLocked) return;
            const next = isPhone ? sanitizePhoneInput(e.target.value) : e.target.value;
            if (field.storage === 'column') updateColumnField(field.id, next);
            else updateJsonField(field.id, next);
          }}
        />
      );
    }

    const isInteger = field.type === 'integer';
    return (
      <TextField
        {...commonProps}
        label={label}
        type="number"
        value={value ?? ''}
        InputLabelProps={{ sx: fieldLabelSx }}
        inputProps={{
          min: field.min ?? 0,
          max: field.max,
          step: isInteger ? 1 : 'any',
        }}
        InputProps={field.unit ? {
          endAdornment: (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', pr: 0.5 }}>
              {field.unit}
            </Typography>
          ),
        } : undefined}
        onChange={(e) => {
          if (fieldLocked) return;
          const next = e.target.value;
          if (field.storage === 'column') updateColumnField(field.id, next);
          else updateJsonField(field.id, next);
        }}
      />
    );
  };

  const visibleGroups = sortSchemaGroups(schema.groups)
    .map((group) => {
      const groupFields = (group.fields || []).filter(
        (field) => !ADDITIONAL_FIELD_ID_SET.has(field.id),
      );
      const fields = isSurfaceGroup(group)
        ? orderSurfaceFields(groupFields).filter(shouldRenderField)
        : groupFields.filter(shouldRenderField);
      return { group, fields, surfaceLayout: isSurfaceGroup(group) };
    })
    .filter(({ fields }) => fields.length > 0);

  const schemaFields = flattenSchemaFields(schema);
  const additionalFields = ADDITIONAL_FIELD_IDS
    .map((fieldId) => schemaFields.find((field) => field.id === fieldId))
    .filter(Boolean)
    .filter(shouldRenderField);
  const groupsWithAdditional = additionalFields.length > 0
    ? [
      ...visibleGroups,
      {
        group: { id: 'uf-additional-fields', label: 'Adicionales' },
        fields: additionalFields,
        surfaceLayout: false,
      },
    ]
    : visibleGroups;

  if (hideEmpty && groupsWithAdditional.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {groupsWithAdditional.map(({ group, fields, surfaceLayout }) => (
        <Box key={group.id}>
          <Typography sx={groupTitleSx}>{group.label}</Typography>
          {surfaceLayout ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                alignItems: 'stretch',
              }}
            >
              {fields.map((field) => (
                <Box key={field.id} sx={{ flex: 1, minWidth: 0 }}>
                  {renderField(field, { labelOverride: SURFACE_FIELD_LABELS[field.id] })}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
            >
              {fields.map((field) => (
                <Box key={field.id} sx={{ minWidth: 0 }}>
                  {renderField(field)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default UfAttributesForm;
